import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Delete from "../../assets/svgs/Recycle Bin.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialExecutedFilterData } from "../../utils/initialData";
import ExecutedLoiArchiveFilter from "./ArchiveFilter/ExecutedLoiArchiveFilter";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const ExecutedloiArchive = () => {
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const queryParams = new URLSearchParams(location.search);
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialExecutedFilterData;
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const listIds = listData?.map((el) => el.id);


  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { count, start_count, end_count , fell_count ,fell_start_count , fell_end_count , broker} = filters;
    if (count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
     if (fell_end_count !== undefined) {
      params += `&fell_end_count=${encodeURIComponent(fell_end_count)}`;
    }
    if (fell_count !== undefined) {
      params += `&fell_count=${encodeURIComponent(fell_count)}`;
    }
    if (fell_start_count !== undefined) {
      params += `&fell_start_count=${encodeURIComponent(fell_start_count)}`;
    }
    if (broker) {
      params += `&broker=${encodeURIComponent(broker)}`;
    }
    if (sortDirection !== undefined && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    localStorage.setItem("queryParams", params);

    return params;
  };

  const fetchData = (filters = filterData) => {
    const queryParams = buildQueryParams(filters);
    axios
      .get(`${BASE_URL}/archive-listing?${queryParams}&type=executed-loi`, config)
      .then((res) => {
        const value = res?.data?.executedlist;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedId("");
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  const handleSelectCheck = (id) => {
    if (selectedItem.length === 0) {
      setSelectedItem([id]);
    } else {
      const index = selectedItem?.indexOf(id);
      if (index === -1) {
        setSelectedItem([...selectedItem, id]);
      } else {
        const filterData = selectedItem?.filter((el) => el !== id);
        setSelectedItem(filterData);
      }
    }
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = () => {
    const dataToSend = {
      action: "delete",
      type: "executed-loi",
      ids: [selectedId],
    };
    setDisableDelete(true);

    axios
      .post(`${BASE_URL}/handle-bulkaction`, dataToSend, config)
      .then((res) => {
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name) {
        return '';
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link.street_address}</span>
          <br />
          <span>{el.link.city}, {el.link.state}</span>
        </>
      );
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || '';
    }
    return el?.link?.name || '';
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">
          ExecutedLOI Archive
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">

            <ExecutedLoiArchiveFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Executed LOI"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search name"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          {loading ? (
            <Loader />
          ) : listData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No ExecutedLOI Found</p>
          ) : (
            <table className="contact-table two-rows-static light-bg-L">
          <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th className="green-H">
                      <div className="table-header">
                        EXECUTED LOI DATE
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th className="green-H">
                      <div className="table-header">
                        FELL OUT OF CONTRACT
                        <img role="button" src={sortBy !== "fell_out_of_contract" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("fell_out_of_contract"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        PROPERTY NAME
                        <img role="button" src={sortBy !== "broker" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("broker"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                       <th>
                      <div className="table-header">
                        Acquisition
                        <img
                          role="button"
                          src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("acquistion");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Disposition
                        <img
                          role="button"
                          src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("disposition");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        NOI
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT PRICE
                        <img role="button" src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT CAP RATE
                        <img role="button" src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        % OF ASKING PRICE
                        <img role="button" src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("asking_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        GCI TO COMPANY
                        <img role="button" src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("company_gcl"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th><div className="table-header">Actions</div></th>
                  </tr>
                </thead>

              <tbody>
                {listData?.map((el, idx) => (
                  <React.Fragment key={idx}>
                    <tr key={idx} className="dark-H body-N">
                      {/* <td className="">
                        <label className="container">
                          <input type="checkbox" checked={selectedItem?.includes(el.id)} onChange={() => handleSelectCheck(el?.id)} />
                          <span className="checkmark"></span>
                        </label>
                      </td> */}
                      <td>
                        <img
                          role="button"
                          src={expandedRows[el.id] ? UpArrow : DownArrow}
                          alt="icon"
                          className="sidebar-icons"
                          onClick={() => toggleRowDetails(el.id)}
                        />
                      </td>
                      <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}
                      </td>

                      <td>{el?.fell_out_of_contract}</td>

                      <td className="head-5 capitalize">
                        <Link to={
                          el?.portfolio === 0
                            ? `/property/${el?.property_id}`
                            : el?.link?.owner_info_type === "contact"
                              ? `/contact/${el?.link?.owner?.id}`
                              : `/company/${el?.link?.owner?.id}`
                        }
                          onClick={(e) => {
                            e.preventDefault();

                            if (el?.portfolio === 0) {
                              navigate(`/property/${el?.property_id}`);
                            } else if (el?.link?.owner_info_type === "contact") {
                              navigate(`/contact/${el?.link?.owner?.id}`);
                            } else if (el?.link?.owner_info_type === "company") {
                              navigate(`/company/${el?.link?.owner?.id}`);
                            }
                          }}>
                          {el?.link?.property_name} <br />
                          {el?.link?.street_address} <br />
                          {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} ,  ${el?.link?.state} ` : ""}
                        </Link>
                      </td>
                            <td>
                          <Link
                                      to={
                             el?.offer?.acquistion_name_id?.type === "contact"
                               ? `/contact/${el?.offer?.acquistion_name_id?.id}`
                               : el?.offer?.acquistion_name_id?.type === "property"
                                 ? `/property/${el?.offer?.acquistion_name_id?.id}`
                                 : "#"
                           }
                           className="body-N dark-H"
                           onClick={(e) => {
                             e.preventDefault();
                             const acquisition = el?.offer?.acquistion_name_id;
                             if (acquisition?.type === "contact") {
                               navigate(`/contact/${acquisition?.id}`);
                             } else if (acquisition?.type === "property") {
                               navigate(`/property/${acquisition?.id}`);
                             }
                           }}
                         >
                           {el?.offer?.acquistion_name || ""}
                          </Link>
                        </td>
                        <td>  
                          <Link
                                      to={
                               el?.offer?.disposition_name_id?.type === "contact"
                                 ? `/contact/${el?.offer?.disposition_name_id?.id}`
                                 : el?.offer?.disposition_name_id?.type === "property"
                                   ? `/property/${el?.offer?.disposition_name_id?.id}`
                                   : "#"
                             }
                             className="body-N dark-H"
                             onClick={(e) => {
                               e.preventDefault();
                               const disposition = el?.offer?.disposition_name_id;
                               if (disposition?.type === "contact") {
                                 navigate(`/contact/${disposition?.id}`);
                               } else if (disposition?.type === "property") {
                                 navigate(`/property/${disposition?.id}`);
                               }
                             }}
                           >
                             {el?.offer?.disposition_name || ""}
                           </Link>
                        </td>
                      <td >${el?.link?.noi_format}</td>
                      <td className="text-center">${el?.contract_price_format}</td>
                      <td className="text-center">
                        <p className="capitalize">{el?.contract_cap_rate_format}%</p>
                      </td>
                      <td className="text-center">
                        {el?.asking_price_format_percentage}%
                      </td>
                      <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                      <td className="flex content-center">
                        <img
                          className="mx-4 sidebar-icons"
                          src={Delete}
                          alt="delete"
                          role="button"
                          onClick={() => {
                            setSelectedId(el?.id);
                            setShowDeleteModal(true);
                          }}
                        />
                      </td>
                    </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                {/* --- TOP INFO SECTION --- */}
                                <div className="grid grid-cols-5 gap-6">

                                <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_broker?.length > 0
                                        ? el.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.seller_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>
    

                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_bds?.first_name} {el?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_broker?.length > 0
                                        ? el.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.buyer_broker.length - 1 && <br />}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_bds?.first_name} {el?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>

                                </div>

                                {/* --- GROSS COMMISSION SECTION --- */}
                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="grid grid-cols-4 gap-6">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>





                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <ContactPagination
          from="archive"
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
          paginationData={paginationData}
          handleNext={handleNext}
          handlePrev={handlePrev}
          onSuccess={onSuccess}
          itemPerPage={itemPerPage}
          handleItemPerPageSelect={(value) => {
            setItemPerPage(value);
            setCurrentPage(1);
          }}
        />

        <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
      </div>
    </BaseLayout>
  );
};

export default ExecutedloiArchive;